// =============================================================================
// Accountant Investors Page
// =============================================================================
// List of investor profiles for K-1 preparation
// =============================================================================

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  MagnifyingGlassIcon,
  UserCircleIcon,
  BuildingOfficeIcon,
  ChevronRightIcon,
  DocumentTextIcon,
} from '@heroicons/react/24/outline';
import { Card } from '../../components/ui';
import { accountantService, type AccountantInvestorProfile } from '../../services/accountant.service';

// =============================================================================
// Component
// =============================================================================

export function AccountantInvestorsPage() {
  const [isLoading, setIsLoading] = useState(true);
  const [profiles, setProfiles] = useState<AccountantInvestorProfile[]>([]);
  const [filteredProfiles, setFilteredProfiles] = useState<AccountantInvestorProfile[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadInvestors();
  }, []);

  useEffect(() => {
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      setFilteredProfiles(
        profiles.filter(
          (p) =>
            p.displayName.toLowerCase().includes(query) ||
            p.investorName.toLowerCase().includes(query) ||
            p.investorEmail.toLowerCase().includes(query) ||
            p.type.toLowerCase().includes(query)
        )
      );
    } else {
      setFilteredProfiles(profiles);
    }
  }, [searchQuery, profiles]);

  const loadInvestors = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await accountantService.listInvestors();
      setProfiles(data);
      setFilteredProfiles(data);
    } catch (err) {
      console.error('Failed to load investors:', err);
      setError('Failed to load investor profiles. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const getProfileTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      INDIVIDUAL: 'Individual',
      LLC: 'LLC',
      TRUST: 'Trust',
      CORPORATION: 'Corporation',
      PARTNERSHIP: 'Partnership',
      IRA: 'IRA',
    };
    return labels[type] || type;
  };

  if (isLoading) {
    return (
      <div className="animate-pulse space-y-6">
        <div className="h-8 w-48 bg-gray-200 rounded" />
        <div className="h-12 bg-gray-200 rounded-lg" />
        <div className="space-y-4">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="h-24 bg-gray-200 rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <div className="text-red-500 mb-4">
          <svg className="h-12 w-12 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        </div>
        <h3 className="text-lg font-medium text-gray-900">{error}</h3>
        <button
          onClick={loadInvestors}
          className="mt-4 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <h1 className="text-2xl font-semibold text-gray-900 tracking-tight">
          Investor Profiles
        </h1>
        <p className="mt-1 text-sm text-gray-500">
          View investor information for K-1 preparation
        </p>
      </div>

      {/* Search */}
      <div className="relative">
        <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
        <input
          type="text"
          placeholder="Search by name, email, or profile type..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
        />
      </div>

      {/* Results Count */}
      <div className="text-sm text-gray-500">
        {filteredProfiles.length} {filteredProfiles.length === 1 ? 'profile' : 'profiles'} found
      </div>

      {/* Profiles List */}
      {filteredProfiles.length === 0 ? (
        <Card padding="lg">
          <div className="text-center py-8">
            <UserCircleIcon className="h-12 w-12 text-gray-400 mx-auto" />
            <h3 className="mt-4 text-lg font-medium text-gray-900">No Profiles Found</h3>
            <p className="mt-2 text-sm text-gray-500">
              {searchQuery
                ? 'Try adjusting your search query'
                : 'No investor profiles are available'}
            </p>
          </div>
        </Card>
      ) : (
        <div className="space-y-3">
          {filteredProfiles.map((profile) => (
            <Link key={profile.id} to={`/accountant/investors/${profile.id}`}>
              <Card padding="md" hover className="transition-all">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="p-3 bg-primary-100 rounded-lg">
                      {profile.type === 'INDIVIDUAL' ? (
                        <UserCircleIcon className="h-6 w-6 text-primary-600" />
                      ) : (
                        <BuildingOfficeIcon className="h-6 w-6 text-primary-600" />
                      )}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-gray-900">{profile.displayName}</h3>
                        <span className="px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-700 rounded">
                          {getProfileTypeLabel(profile.type)}
                        </span>
                      </div>
                      <p className="text-sm text-gray-500">{profile.investorName} - {profile.investorEmail}</p>
                      <div className="flex items-center gap-4 mt-1 text-sm">
                        <span className="text-gray-600">
                          Total Invested: <span className="font-medium">{formatCurrency(profile.totalInvested)}</span>
                        </span>
                        <span className="text-gray-400">|</span>
                        <span className="text-gray-600">
                          {profile.investmentCount} {profile.investmentCount === 1 ? 'investment' : 'investments'}
                        </span>
                        {profile.hasSSN && (
                          <>
                            <span className="text-gray-400">|</span>
                            <span className="text-gray-600">SSN: ***-**-{profile.ssnLastFour}</span>
                          </>
                        )}
                        {profile.hasFEIN && (
                          <>
                            <span className="text-gray-400">|</span>
                            <span className="text-gray-600">FEIN: **-***{profile.feinLastFour}</span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    {profile.k1Count > 0 && (
                      <div className="flex items-center gap-1 text-sm text-gray-500">
                        <DocumentTextIcon className="h-4 w-4" />
                        <span>{profile.k1Count} K-1{profile.k1Count !== 1 ? 's' : ''}</span>
                      </div>
                    )}
                    <ChevronRightIcon className="h-5 w-5 text-gray-400" />
                  </div>
                </div>
              </Card>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

export default AccountantInvestorsPage;
